<template>
  <b-modal
    :id="modalId"
    title="Add crew equipment"
    :ok-title="'Add'"
    :cancel-title="'Back'"
    :ok-disabled="!canSave"
    @ok="handleOk"
    @hidden="reset"
  >
    <b-form-group label="Day">
      <div class="crew-plant-date-strip">
        <button
          v-for="d in weekDates"
          :key="d.iso"
          type="button"
          class="crew-plant-date-chip"
          :class="{ active: selectedDate === d.iso }"
          @click="selectedDate = d.iso"
        >
          {{ d.label }}
          <span class="crew-plant-date-num">{{ d.dayNum }}</span>
        </button>
      </div>
    </b-form-group>

    <b-form-group label="Equipment">
      <plant-entry-row
        v-for="(entry, index) in rows"
        :key="index"
        :value="entry"
        :equipment-options="equipmentOptions"
        @input="updateRow(index, $event)"
        @remove="removeRow(index)"
      />
      <button type="button" class="crew-plant-add-link" @click="addRow">
        + Add another item
      </button>
    </b-form-group>

    <p class="crew-plant-hint">
      Logged once for the whole crew on site - not multiplied per worker.
    </p>
  </b-modal>
</template>

<script>
import PlantEntryRow from '@/components/timesheets/shared/PlantEntryRow.vue'

export default {
  name: 'AddCrewPlantModal',
  components: { PlantEntryRow },
  props: {
    // Two instances of this modal can be mounted at once (business admin's
    // "Supervisor Timesheets" tab and a supervisor's own "My Submissions"
    // tab both embed it, and bootstrap-vue tabs don't unmount once visited).
    // A shared id causes $bvModal.show/hide to target the wrong instance,
    // so each parent must pass a distinct one.
    modalId: { type: String, default: 'add-crew-plant-modal' },
    // [{ iso: 'YYYY-MM-DD', label: 'MON', dayNum: '10' }]
    weekDates: { type: Array, default: () => [] },
    equipmentOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      selectedDate: null,
      rows: [{ plant_equipment_id: null, quantity: 1 }],
    }
  },
  computed: {
    canSave() {
      return (
        !!this.selectedDate &&
        this.rows.some((r) => r.plant_equipment_id && r.quantity != null)
      )
    },
  },
  watch: {
    weekDates: {
      immediate: true,
      handler(dates) {
        if (dates.length && !this.selectedDate) {
          const todayIso = new Date().toISOString().slice(0, 10)
          const match = dates.find((d) => d.iso === todayIso)
          this.selectedDate = (match || dates[0]).iso
        }
      },
    },
  },
  methods: {
    addRow() {
      this.rows.push({ plant_equipment_id: null, quantity: 1 })
    },
    updateRow(index, value) {
      this.rows.splice(index, 1, value)
    },
    removeRow(index) {
      this.rows.splice(index, 1)
    },
    handleOk(bvModalEvent) {
      bvModalEvent.preventDefault()
      const entries = this.rows.filter(
        (r) => r.plant_equipment_id && r.quantity != null,
      )
      this.$emit('save', { date: this.selectedDate, entries })
      this.$bvModal.hide(this.modalId)
    },
    reset() {
      this.rows = [{ plant_equipment_id: null, quantity: 1 }]
      this.selectedDate = null
    },
  },
}
</script>

<style lang="scss" scoped>
.crew-plant-date-strip {
  display: flex;
  gap: 6px;
}

.crew-plant-date-chip {
  flex: 1;
  text-align: center;
  padding: 8px 0 7px;
  border-radius: 7px;
  border: 1px solid #dee2e6;
  background: #fff;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.03em;

  &.active {
    border-color: #006cff;
    background: #eaf1ff;
    color: #006cff;
  }
}

.crew-plant-date-num {
  display: block;
  font-size: 13px;
  font-weight: 700;
  margin-top: 1px;
  color: inherit;
}

.crew-plant-add-link {
  border: 0;
  background: none;
  padding: 0;
  font-size: 12.5px;
  font-weight: 600;
  color: #006cff;
  margin-top: 4px;
}

.crew-plant-hint {
  font-size: 12px;
  color: #9ca3af;
  margin: 14px 0 0;
}
</style>
